import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Award, Shield, Wrench, Users, Target, TrendingUp,
  CheckCircle2, ArrowLeft, Phone, Star, Building, Eye,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEO from "@/components/SEO";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeHtml } from "@/lib/sanitize";
import { iconMap } from "@/lib/serviceMeta";
import orBagagLogo from "@/assets/or-bagag-logo.png";

const milestones = [
  { year: "2013", title: "תחילת הדרך", desc: "כניסה לתחום הגגות והפחחות – למידה מהשטח, בורג אחרי בורג" },
  { year: "2016", title: "התמחות מקצועית", desc: "התמקדות באיתור נזילות ובנייה קלה – פיתוח חוש טכני מתקדם" },
  { year: "2019", title: "הקמת \"אור בגג\"", desc: "הקמת המותג העצמאי עם חזון ברור – להיות התחנה האחרונה ללקוח" },
  { year: "2023", title: "500+ לקוחות", desc: "ציון דרך של מעל 500 לקוחות מרוצים מכל הארץ" },
  { year: "2026", title: "Level אחר", desc: "פרויקטים גדולים, שיתופי פעולה עם אדריכלים, וצמיחה מתמדת" },
];

const FALLBACK_VALUES: Array<{ icon: LucideIcon; title: string; desc: string }> = [
  { icon: Target, title: "ביצוע ברמת 95%+", desc: "ביצוע ברמת מעל 95% – חוש טכני, דיוק ויסודיות בכל פרויקט." },
  { icon: Shield, title: "אחריות מלאה", desc: "אחריות על העבודה גם במקרים בעייתיים שאחרים לא לוקחים." },
  { icon: Wrench, title: "פתרון 99% מהבעיות", desc: "התחנה האחרונה – פתרון כמעט מוחלט לכל בעיה." },
  { icon: Users, title: "לקוחות איכותיים", desc: "מלקוחות פרטיים ועד חברות ומפעלים גדולים." },
  { icon: Eye, title: "שקיפות מלאה", desc: "תהליך עבודה מסודר ושקוף – מההתחלה ועד הסוף." },
  { icon: TrendingUp, title: "צמיחה מתמדת", desc: "החזון – להפוך למומחה המוביל בתחום." },
];

const About = () => {
  const storyRef = useScrollReveal();
  const valuesRef = useScrollReveal();
  const timelineRef = useScrollReveal();
  const audienceRef = useScrollReveal();

  const { data: aboutPosts = [] } = useQuery({
    queryKey: ["posts", "about"],
    queryFn: async () => {
      const { data } = await supabase
        .from("posts")
        .select("*")
        .eq("section", "about")
        .order("sort_order");
      return data || [];
    },
  });

  const story = aboutPosts.find((p) => {
    const m = p.metadata as { type?: string } | null;
    return m?.type === "story";
  });
  const valueItems = aboutPosts.filter((p) => {
    const m = p.metadata as { type?: string } | null;
    return m?.type === "value";
  });
  const values = valueItems.length > 0
    ? valueItems.map((p) => {
        const m = p.metadata as { icon?: string } | null;
        const Icon = (m?.icon && iconMap[m.icon]) || Award;
        return {
          icon: Icon,
          title: p.title,
          html: p.content_html || "",
        };
      })
    : FALLBACK_VALUES.map((v) => ({ icon: v.icon, title: v.title, html: `<p>${v.desc}</p>` }));

  return (
    <div className="min-h-screen">
      <SEO
        title="מומחה גגות חיפה – קבלן גגות ופחחות מומלץ מ-2013"
        description="אור בגג – מומחה וקבלן גגות בחיפה והקריות. 13 שנות ניסיון בתיקון גג רעפים, איטום גגות, פחחות ומרזבים, עם אחריות מלאה לפרויקטים פרטיים ועסקיים."
        path="/about"
      />
      <Header />
      <main id="main">
      {/* Hero */}
      <section className="bg-dark pt-28 pb-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-dark-foreground mb-4">
            הכירו את <span className="text-primary">אור</span>
          </h1>
          <p className="text-lg text-dark-foreground/90 max-w-2xl mx-auto leading-relaxed">
            הפנים מאחורי המותג "אור בגג" – מומחה גגות ופחחות עם 13 שנות ניסיון,
            חזון ברור ומחויבות בלתי מתפשרת לתוצאה מושלמת
          </p>
          <div className="mt-6">
            <Button asChild size="sm" className="bg-primary text-primary-foreground hover:bg-gold-light font-bold">
              <Link to="/">
                <ArrowLeft size={16} />
                חזרה לדף הבית
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section ref={storyRef} className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="animate-on-scroll grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            {/* Photo placeholder */}
            <div className="lg:col-span-2">
              <div className="aspect-[3/4] rounded-2xl bg-dark overflow-hidden relative flex items-center justify-center">
                <img
                  src={story?.image_url || orBagagLogo}
                  alt={story?.title || "אור בגג"}
                  className="w-full h-full object-contain p-6"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Story text */}
            <div className="lg:col-span-3">
              <h2 className="text-3xl font-bold text-foreground mb-6">
                {story?.title || "הסיפור שלי"}
              </h2>
              {story?.content_html ? (
                <div
                  className="prose prose-sm md:prose-base max-w-none text-muted-foreground leading-relaxed [&_strong]:text-foreground [&_p]:mb-4"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(story.content_html) }}
                />
              ) : (
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>שמי אור, ואני בתחום הגגות והפחחות מאז 2013.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* Values */}
      <section ref={valuesRef} className="py-20 bg-secondary/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              למה <span className="text-primary">בוחרים בנו?</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              הערכים שמנחים אותנו בכל פרויקט – מהקטן ביותר ועד הגדול ביותר
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, i) => (
              <Card
                key={i}
                className="animate-on-scroll group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-border bg-card"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                    <value.icon className="text-primary" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{value.title}</h3>
                  <div
                    className="text-muted-foreground leading-relaxed text-sm prose prose-sm max-w-none [&_p]:m-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(value.html) }}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section ref={timelineRef} className="py-20 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              הדרך <span className="text-primary">שלנו</span>
            </h2>
            <p className="text-muted-foreground text-lg">מ-2013 ועד היום – ציוני הדרך שעיצבו את "אור בגג"</p>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute right-6 md:right-1/2 top-0 bottom-0 w-0.5 bg-primary/20 md:-translate-x-0.5" />

            <div className="space-y-10">
              {milestones.map((item, i) => (
                <div
                  key={i}
                  className={`animate-on-scroll relative flex flex-col md:flex-row items-start ${
                    i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  } gap-4 md:gap-8`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  {/* Dot */}
                  <div className="absolute right-4 md:right-auto md:left-1/2 w-4 h-4 rounded-full bg-primary border-4 border-background z-10 md:-translate-x-1/2 -translate-y-0" />

                  {/* Content */}
                  <div className={`mr-12 md:mr-0 md:w-[45%] ${i % 2 === 0 ? "md:text-left md:pr-0" : "md:text-right md:pl-0"}`}>
                    <Card className="border-border bg-card">
                      <CardContent className="p-5">
                        <span className="text-primary font-bold text-lg">{item.year}</span>
                        <h3 className="font-bold text-foreground mt-1 mb-1">{item.title}</h3>
                        <p className="text-sm text-muted-foreground">{item.desc}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Spacer for alternation */}
                  <div className="hidden md:block md:w-[45%]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* Target Audience */}
      <section ref={audienceRef} className="py-20 bg-secondary/50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              למי אנחנו <span className="text-primary">מתאימים?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-on-scroll">
            {/* Private Clients */}
            <Card className="border-border bg-card">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Users className="text-primary" size={28} />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">לקוחות פרטיים</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  מתיקון נזילה קטנה, החלפת רעף שבור ועד שיפוץ מלא של הגג –
                  אנחנו מטפלים בכל בעיה ביסודיות ובמקצועיות.
                </p>
                <ul className="space-y-2">
                  {["תיקון נזילות ואיטום", "החלפת רעפים ואיסכורית", "התקנת מרזבים חדשים", "אוורור ותחזוקה שוטפת"].map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="text-primary flex-shrink-0" size={16} />
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Companies & Architects */}
            <Card className="border-primary/30 bg-dark">
              <CardContent className="p-8">
                <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                  <Building className="text-primary" size={28} />
                </div>
                <h3 className="text-xl font-bold text-dark-foreground mb-3">חברות ואדריכלים</h3>
                <p className="text-dark-foreground/90 leading-relaxed mb-4">
                  פרויקטים גדולים של החלפת תעלות, חיפויי פח לבניינים, ושיתוף פעולה טכני צמוד עם
                  אדריכלים ומתכננים – מהשלב התכנוני ועד הביצוע.
                </p>
                <ul className="space-y-2">
                  {["פרויקטים מורכבים בהיקפים גדולים", "שיתוף פעולה עם אדריכלים", "עמידה בלוחות זמנים", "הצעות מחיר מפורטות"].map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="text-primary flex-shrink-0" size={16} />
                      <span className="text-dark-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-dark">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-dark-foreground mb-4">
            רוצים לעבוד עם <span className="text-primary">הטובים ביותר?</span>
          </h2>
          <p className="text-dark-foreground/90 text-lg mb-8 max-w-xl mx-auto">
            צרו קשר עוד היום – ותנו לנו להוכיח שאנחנו באמת התחנה האחרונה שלכם
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold">
              <a href="/#contact">תיאום ביקור מקצועי</a>
            </Button>
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-gold-light text-lg px-8 py-6 font-bold">
              <a href="tel:+972545725681">
                <Phone size={20} />
                054-572-5681
              </a>
            </Button>
          </div>
        </div>
      </section>

      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default About;
